import { useEffect, useRef, useState } from "react";
import { Button, Input, InputGroup } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faPencilAlt,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { useHotkeys } from "react-hotkeys-hook";
import { CyTitleEditable } from "../CyTitleEditable";
import { CyInputEditable } from "../../common/CyInputEditable";
import { useTranslation } from "react-i18next";

interface Props {
  content: string;
  updateContent: (content: string) => void;
  editAllowed: boolean;
  headingClassName?: string;
}

export function TitleEditable(props: Props) {
  const { t } = useTranslation();

  const inputfield = useRef<HTMLInputElement>(null);

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");

  useHotkeys("escape", () => cancelEditMode(), { enableOnFormTags: ["input"] });
  useHotkeys("enter", () => saveAndCloseEditMode(), {
    enableOnFormTags: ["input"],
  });

  function enterEditMode() {
    setIsEditing(true);
    setInputValue(props.content);
  }

  useEffect(() => {
    const element = inputfield.current;
    if (element) {
      element.focus();
      element.select();
    }
  }, [isEditing]);

  function cancelEditMode() {
    setIsEditing(false);
  }

  function saveAndCloseEditMode() {
    props.updateContent(inputValue);
    setIsEditing(false);
  }

  function updateInputValue(evt: React.FormEvent<HTMLInputElement>) {
    setInputValue(evt.currentTarget.value);
  }

  return (
    <>
      {isEditing ? (
        <InputGroup>
          <Input
            type="text"
            name="playlistName"
            placeholder={t("playlist.edit.editTitle")}
            value={inputValue}
            onChange={(evt) => updateInputValue(evt)}
            innerRef={inputfield}
            data-cy={CyInputEditable.InputField}
          />
          <Button
            color="primary"
            onClick={() => saveAndCloseEditMode()}
            data-cy={CyInputEditable.SaveButton}
          >
            <FontAwesomeIcon icon={faCheck} />
          </Button>
          <Button color="primary" onClick={() => cancelEditMode()}>
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </InputGroup>
      ) : (
        <>
          <h2
            data-cy={CyTitleEditable.SpanPlaylistTitle}
            className={props.headingClassName ?? "mb-0 ps-0"}
          >
            {props.content}
          </h2>
          {props.editAllowed && (
            <Button
              color="link"
              onClick={() => enterEditMode()}
              data-cy={CyInputEditable.EditButton}
            >
              <FontAwesomeIcon icon={faPencilAlt} />
            </Button>
          )}
        </>
      )}
    </>
  );
}
